import {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    Snackbar,
    Switch,
    Typography,
    useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageHeader from "../../../components/PageHeader";
import MySelect from "../../../components/MySelect";
import MyTextField from "../../../components/MyTextField";
import {useCurrentUser} from "../../../hooks/useCurrentUser";
import {useBusinessUnitCodes} from "../../../hooks/useBusinessUnitCodes";
import {useUserTypes} from "../../../hooks/useUserTypes";
import {useClaimCategories} from "../../../hooks/useClaimCategories";
import {useClaims} from "../../../hooks/useClaims";
import {useCountries} from "../../../hooks/useCountries";
import {useAvailableGroups} from "../../../hooks/useAvailableGroups";
import {useAuthUser} from "../../../hooks/useAuthUser";
import {useAuthUserUpdate} from "../../../hooks/useAuthUserUpdate";
import {useMyPermissions} from "../../../hooks/useMyPermissions";
import {colorForIndex} from "../createUser/palette";
import {getStep4Config, step4HasFields} from "../createUser/userTypeConfig";
import GroupChips from "./GroupChips";
import PermissionsManager from "./PermissionsManager";
import AdditionalDetails from "./AdditionalDetails";
import ResetPasswordDialog from "./ResetPasswordDialog";

const ADMIN_CLAIM_VALUE = "AccessAdminPortal";
const RESET_PASSWORD_CLAIM_VALUE = "ResetUserPasswords";
const sectionTitleSx = {fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary"};
const fieldLabelSx = {fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.25};
const claimKey = (c) => `${c.claimType}::${c.claimValue}`;

// Module-level so their subtrees keep a stable identity across renders (defining them inside the page
// would remount and drop input focus while editing).
const Section = ({title, children}) => {
    const theme = useTheme();
    return (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "10px", mb: 2, overflow: "hidden"}}>
            <Box sx={{px: 3, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.secondary}}>
                <Typography sx={sectionTitleSx}>{title}</Typography>
            </Box>
            <Box sx={{p: 3}}>{children}</Box>
        </Box>
    );
};

const FieldView = ({label, value, muted}) => (
    <Box>
        <Typography sx={fieldLabelSx}>{label}</Typography>
        <Typography sx={{fontSize: "0.9rem", color: muted ? "text.secondary" : "text.primary", fontStyle: muted ? "italic" : "normal"}}>{value}</Typography>
    </Box>
);

// TMMFX-2164 / FE-8 — View / Edit User page. Loads a user's full authz-aware detail (API-1), toggles
// between read-only view and edit, and saves all changes atomically (API-2, TMMFX-3451).
const UserDetails = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const {id} = useParams();

    const {user, isLoading, isError, refetch} = useAuthUser(id);
    const [currentUser] = useCurrentUser();
    const businessUnits = useBusinessUnitCodes();
    const [userTypes] = useUserTypes({managedByUserId: currentUser?.id, enabled: !!currentUser?.id});
    const [categories] = useClaimCategories();
    const [allClaims] = useClaims();
    const myPermissions = useMyPermissions();
    const canResetPassword = myPermissions.includes(RESET_PASSWORD_CLAIM_VALUE);
    // The signed-in user may not change their own active state (the server refuses it too), so the
    // control is withheld on their own row — the state still displays.
    const isOwnAccount = currentUser?.id != null && Number(currentUser.id) === Number(id);

    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [classNonce, setClassNonce] = useState(0);
    const [pendingGroupReset, setPendingGroupReset] = useState(false);
    const [saved, setSaved] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

    const updateMutation = useAuthUserUpdate();

    // Committed (server) state, normalized to the editable shape.
    const committed = useMemo(() => {
        if (!user) return null;
        return {
            login: user.login ?? "",
            firstName: user.firstName ?? "",
            lastName: user.lastName ?? "",
            countryCode: user.countryCode ?? "",
            phoneNumber: user.phoneNumber ?? "",
            businessUnitCode: user.businessUnitCode ?? "",
            userTypeId: user.userTypeId ?? "",
            isActive: !!user.isActive,
            groupIds: (user.groups ?? []).map((g) => g.id),
            individualClaims: (user.individualClaims ?? []).map((c) => ({claimType: c.claimType, claimValue: c.claimValue})),
            orderPin: user.orderPin ?? "",
            shiptoId: user.shiptoId ?? "",
            billtoId: user.billtoId ?? "",
            retailLocationId: user.retailLocationId ?? "",
            adminPortalAccess: !!user.adminPortalAccess,
            salesRegionId: user.salesRegionId ?? "",
            salesTerritories: (user.salesTerritoryIds ?? []).map((id) => ({id})),
            facilities: (user.facilities ?? []).map((f) => ({id: f.id, name: f.name})),
        };
    }, [user]);

    const data = editMode ? draft : committed;

    const cfg = getStep4Config(data?.userTypeId);
    const {groups: availableGroups, isLoading: groupsLoading} = useAvailableGroups(data?.userTypeId, data?.businessUnitCode);
    const countries = useCountries(data?.businessUnitCode, !!data?.businessUnitCode);

    // Group name + claims by id, from the available list (edit) or the committed user's groups (view /
    // groups no longer available). Lets both modes attribute permission sources.
    const groupInfoById = useMemo(() => {
        const m = new Map();
        (user?.groups ?? []).forEach((g) => m.set(g.id, {name: g.name, claims: g.claims ?? []}));
        (availableGroups ?? []).forEach((g) => m.set(g.id, {name: g.name, claims: g.claims ?? []}));
        return m;
    }, [user, availableGroups]);

    // Stable per-group color: original (committed) groups keep their color by committed position;
    // groups added this session get colors after the originals. Removing/restoring a group therefore
    // never reshuffles the others' colors. Shared by the group chips and the permission-dot
    // attribution so the two always match.
    const groupColorById = useMemo(() => {
        const m = new Map();
        (committed?.groupIds ?? []).forEach((gid, i) => m.set(gid, colorForIndex(i)));
        let next = (committed?.groupIds ?? []).length;
        (data?.groupIds ?? []).forEach((gid) => {
            if (!m.has(gid)) m.set(gid, colorForIndex(next++));
        });
        return m;
    }, [committed, data]);

    const assignedGroupObjs = useMemo(() => {
        if (!data) return [];
        return (data.groupIds ?? []).map((gid, i) => {
            const info = groupInfoById.get(gid) ?? {name: `Group ${gid}`, claims: []};
            return {id: gid, name: info.name, color: groupColorById.get(gid) ?? colorForIndex(i), claims: info.claims};
        });
    }, [data, groupInfoById, groupColorById]);

    const grantsAdmin = assignedGroupObjs.some((g) => (g.claims ?? []).some((c) => c.claimValue === ADMIN_CLAIM_VALUE));

    const userTypeName = useMemo(
        () => (userTypes ?? []).find((u) => u.id === Number(data?.userTypeId))?.displayName ?? "",
        [userTypes, data]
    );
    const countryName = useMemo(
        () => (countries ?? []).find((c) => c.id === data?.countryCode)?.name ?? data?.countryCode ?? "",
        [countries, data]
    );

    // Auto-reconcile groups after a BU / User Type change: keep still-available assignments, else the
    // new combo's default (mirrors the creation reset).
    useEffect(() => {
        if (!editMode || !pendingGroupReset || groupsLoading) return;
        const availableIds = (availableGroups ?? []).map((g) => g.id);
        setDraft((prev) => {
            if (!prev) return prev;
            let kept = (prev.groupIds ?? []).filter((gid) => availableIds.includes(gid));
            if (kept.length === 0) {
                const def = (availableGroups ?? []).find((g) => g.isDefault)?.id;
                kept = def != null ? [def] : availableIds.length > 0 ? [availableIds[0]] : [];
            }
            return {...prev, groupIds: kept};
        });
        setPendingGroupReset(false);
    }, [editMode, pendingGroupReset, groupsLoading, availableGroups]);

    // ---- mode / field editing ----
    const enterEdit = () => {
        setDraft(JSON.parse(JSON.stringify(committed)));
        setSubmitError("");
        setEditMode(true);
    };
    const cancelEdit = () => {
        setEditMode(false);
        setDraft(null);
        setDialog(null);
        setPendingGroupReset(false);
    };

    const setField = (key, value) =>
        setDraft((prev) => {
            const next = {...prev, [key]: value};
            // Keep the admin toggle and the AccessAdminPortal individual claim in sync (unless a group grants it).
            if (key === "adminPortalAccess") {
                const others = (prev.individualClaims ?? []).filter((c) => c.claimValue !== ADMIN_CLAIM_VALUE);
                next.individualClaims = value && !grantsAdmin ? [...others, {claimType: "permission", claimValue: ADMIN_CLAIM_VALUE}] : others;
            }
            return next;
        });

    const addClaim = (claim) =>
        setDraft((prev) => {
            if ((prev.individualClaims ?? []).some((c) => claimKey(c) === claimKey(claim))) return prev;
            const next = {...prev, individualClaims: [...(prev.individualClaims ?? []), {claimType: claim.claimType, claimValue: claim.claimValue}]};
            if (claim.claimValue === ADMIN_CLAIM_VALUE) next.adminPortalAccess = true;
            return next;
        });
    const removeClaim = (claim) =>
        setDraft((prev) => {
            const next = {...prev, individualClaims: (prev.individualClaims ?? []).filter((c) => claimKey(c) !== claimKey(claim))};
            if (claim.claimValue === ADMIN_CLAIM_VALUE) next.adminPortalAccess = false;
            return next;
        });

    const addGroup = (gid) => setDraft((prev) => (prev.groupIds.includes(gid) ? prev : {...prev, groupIds: [...prev.groupIds, gid]}));
    const removeGroup = (gid) => setDraft((prev) => (prev.groupIds.length <= 1 ? prev : {...prev, groupIds: prev.groupIds.filter((g) => g !== gid)}));
    const restoreGroup = (gid) => setDraft((prev) => (prev.groupIds.includes(gid) ? prev : {...prev, groupIds: [...prev.groupIds, gid]}));

    const requestClassChange = (key, value, label, human) => {
        if (value === draft[key]) return;
        setDialog({
            title: `Change ${label}`,
            text: `Changing the ${label} to "${human}" will reset this user's group assignments — the default group for the new combination is auto-selected. Continue?`,
            onConfirm: () => {
                setDraft((prev) => ({...prev, [key]: value, salesRegionId: "", salesTerritories: [], facilities: []}));
                setPendingGroupReset(true);
            },
        });
    };
    const closeDialog = (confirmed) => {
        if (confirmed && dialog?.onConfirm) dialog.onConfirm();
        if (!confirmed) setClassNonce((n) => n + 1); // snap the classification selects back to the committed value
        setDialog(null);
    };

    // ---- save ----
    const canSave =
        !!data &&
        data.firstName &&
        data.lastName &&
        data.countryCode &&
        data.businessUnitCode &&
        data.userTypeId &&
        (data.groupIds ?? []).length >= 1;

    const buildPayload = () => {
        const adminEnabled = grantsAdmin ? true : !!data.adminPortalAccess;
        const payload = {
            firstName: data.firstName,
            lastName: data.lastName,
            countryCode: data.countryCode || undefined,
            phoneNumber: data.phoneNumber || undefined,
            businessUnitCode: data.businessUnitCode,
            userTypeId: Number(data.userTypeId),
            groupIds: data.groupIds ?? [],
            individualClaims: (data.individualClaims ?? []).map((c) => ({claimType: c.claimType, claimValue: c.claimValue})),
            adminPortalAccess: cfg.adminAccess ? adminEnabled : false,
        };
        if (cfg.orderPin && data.orderPin) payload.orderPin = String(data.orderPin);
        if (cfg.shipto && data.shiptoId) payload.shiptoId = data.shiptoId;
        if (cfg.billto && data.billtoId) payload.billtoId = data.billtoId;
        if (cfg.retailLoc && data.retailLocationId) payload.retailLocationId = Number(data.retailLocationId);
        payload.salesRegionId = cfg.hasSalesRegion && data.salesRegionId ? Number(data.salesRegionId) : null;
        payload.salesTerritoryIds = cfg.hasSalesTerritories ? (data.salesTerritories ?? []).map((t) => Number(t.id)) : [];
        payload.facilityIds = cfg.hasFacilities ? (data.facilities ?? []).map((f) => Number(f.id)) : [];
        // Omitted for the caller's own account: the server refuses a changed own state, and omitting the
        // field is not a change.
        if (!isOwnAccount) payload.activated = !!data.isActive;
        return payload;
    };

    const save = () => {
        if (!canSave) return;
        setSubmitError("");
        updateMutation.mutate(
            {id, payload: buildPayload()},
            {
                onSuccess: () => {
                    setEditMode(false);
                    setDraft(null);
                    setPendingGroupReset(false);
                    setSaved(true);
                    refetch();
                },
                onError: (err) =>
                    setSubmitError(err.response?.data?.message || err.response?.data?.title || "Failed to save changes. Please try again."),
            }
        );
    };

    // ---- loading / not-found ----
    if (isLoading) {
        return (
            <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
                <PageHeader title="User Detail" />
                <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }
    if (isError || !data) {
        return (
            <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
                <PageHeader title="User Detail" />
                <Box sx={{p: 3}}>
                    <Alert severity="error">Could not load this user.</Alert>
                    <Button sx={{mt: 2}} startIcon={<ArrowBackIcon />} onClick={() => navigate("/users")}>Back to Users</Button>
                </Box>
            </Box>
        );
    }

    // ---- group diff inputs for GroupChips ----
    // Single position-stable list: originally-assigned groups in committed order (each shown as
    // current or, if toggled off, removing-in-place), then groups added this session.
    const originalGroupIds = committed.groupIds;
    const draftGroupIds = data.groupIds ?? [];
    const nameFor = (gid) => groupInfoById.get(gid)?.name ?? `Group ${gid}`;
    const orderedOriginal = originalGroupIds.map((gid) => {
        const inDraft = draftGroupIds.includes(gid);
        return {
            id: gid,
            name: nameFor(gid),
            color: inDraft ? groupColorById.get(gid) : theme.palette.text.disabled,
            state: inDraft ? "current" : "removing",
            added: false,
        };
    });
    const addedItems = draftGroupIds
        .filter((gid) => !originalGroupIds.includes(gid))
        .map((gid) => ({id: gid, name: nameFor(gid), color: groupColorById.get(gid), state: "current", added: editMode}));
    const groupChipItems = [...orderedOriginal, ...addedItems];
    const pendingRemoval = editMode ? originalGroupIds.filter((gid) => !draftGroupIds.includes(gid)) : [];
    const addableGroups = (availableGroups ?? [])
        .filter((g) => !draftGroupIds.includes(g.id) && !pendingRemoval.includes(g.id))
        .map((g) => ({id: g.id, name: g.name}));

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
            <PageHeader title={editMode ? `Editing: ${data.firstName} ${data.lastName}` : `${data.firstName} ${data.lastName}`}>
                <Box sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-end", gap: 2, mt: 0.5}}>
                    <Box sx={{display: "flex", gap: 1, flexShrink: 0}}>
                        {editMode ? (
                            <>
                                <Button onClick={cancelEdit}>Cancel</Button>
                                <Button variant="contained" onClick={save} disabled={!canSave || updateMutation.isPending}>
                                    {updateMutation.isPending ? <><CircularProgress size={16} sx={{mr: 1}} /> Saving…</> : "Save Changes"}
                                </Button>
                            </>
                        ) : (
                            <>
                                {canResetPassword && (
                                    <Button variant="outlined" startIcon={<LockResetOutlinedIcon />} onClick={() => setResetPasswordOpen(true)}>
                                        Reset Password
                                    </Button>
                                )}
                                <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={enterEdit}>Edit</Button>
                            </>
                        )}
                    </Box>
                </Box>
            </PageHeader>

            <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", p: 3, backgroundColor: "white"}}>
                <Box sx={{maxWidth: 960, mx: "auto"}}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/users")} sx={{mb: 2}}>Back to Users</Button>

                    {/* Basic Information (identity + classification) */}
                    <Section title="Basic Information">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MyTextField value={data.login} editable={false} label="Login" type="text" required={false} onChange={() => {}} />
                                ) : (
                                    <FieldView label="Login" value={data.login} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} />
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MyTextField value={data.firstName} editable={true} label="First Name" type="text" required={true} onChange={(e) => setField("firstName", e.target.value)} />
                                ) : (
                                    <FieldView label="First Name" value={data.firstName} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MyTextField value={data.lastName} editable={true} label="Last Name" type="text" required={true} onChange={(e) => setField("lastName", e.target.value)} />
                                ) : (
                                    <FieldView label="Last Name" value={data.lastName} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MySelect label="Country" options={countries} value={data.countryCode} required={true} onChange={(v) => setField("countryCode", v)} />
                                ) : (
                                    <FieldView label="Country" value={countryName || "—"} muted={!countryName} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MyTextField value={data.phoneNumber} editable={true} label="Phone Number" type="text" required={false} onChange={(e) => setField("phoneNumber", e.target.value)} />
                                ) : (
                                    <FieldView label="Phone Number" value={data.phoneNumber || "Not provided"} muted={!data.phoneNumber} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MySelect
                                        key={`bu-${committed.businessUnitCode}-${classNonce}`}
                                        label="Business Unit"
                                        options={businessUnits}
                                        value={data.businessUnitCode}
                                        required={true}
                                        onChange={(v) => requestClassChange("businessUnitCode", v, "Business Unit", v)}
                                    />
                                ) : (
                                    <FieldView label="Business Unit" value={data.businessUnitCode} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {editMode ? (
                                    <MySelect
                                        key={`ut-${committed.userTypeId}-${classNonce}`}
                                        label="User Type"
                                        options={(userTypes ?? []).map((u) => ({id: u.id, name: u.displayName}))}
                                        value={data.userTypeId}
                                        required={true}
                                        onChange={(v) => requestClassChange("userTypeId", v, "User Type", (userTypes ?? []).find((u) => u.id === Number(v))?.displayName ?? "")}
                                    />
                                ) : (
                                    <FieldView label="User Type" value={userTypeName} />
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {editMode && !isOwnAccount ? (
                                    <Box>
                                        <Typography sx={fieldLabelSx}>Active</Typography>
                                        <FormControlLabel
                                            control={<Switch checked={!!data.isActive} onChange={(e) => setField("isActive", e.target.checked)} />}
                                            label={data.isActive ? "Active" : "Inactive"}
                                        />
                                    </Box>
                                ) : (
                                    <FieldView label="Active" value={data.isActive ? "Active" : "Inactive"} />
                                )}
                            </Grid>
                        </Grid>
                    </Section>

                    {/* Groups & Permissions */}
                    <Section title="Groups & Permissions">
                        <GroupChips
                            items={groupChipItems}
                            addable={addableGroups}
                            editMode={editMode}
                            canRemove={(data.groupIds ?? []).length > 1}
                            onAdd={addGroup}
                            onRemove={removeGroup}
                            onRestore={restoreGroup}
                        />
                        {editMode && (data.groupIds ?? []).length < 1 && (
                            <Alert severity="error" sx={{mt: 1.5}}>At least one group is required.</Alert>
                        )}
                        <Box sx={{mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}`}}>
                            <Typography sx={{...sectionTitleSx, mb: 1.5}}>Permissions</Typography>
                            <PermissionsManager
                                assignedGroups={assignedGroupObjs}
                                individualClaims={data.individualClaims ?? []}
                                allClaims={allClaims ?? []}
                                categories={categories ?? []}
                                editMode={editMode}
                                onAdd={addClaim}
                                onRemove={removeClaim}
                            />
                        </Box>
                    </Section>

                    {/* Additional Details */}
                    {step4HasFields(data.userTypeId) && (
                        <Section title="Additional Details">
                            <AdditionalDetails
                                editMode={editMode}
                                cfg={cfg}
                                data={data}
                                setField={setField}
                                grantsAdmin={grantsAdmin}
                                businessUnitCode={data.businessUnitCode}
                                userId={currentUser?.id}
                            />
                        </Section>
                    )}

                    {submitError && <Alert severity="error" sx={{mb: 2, maxWidth: 960}}>{submitError}</Alert>}
                </Box>
            </Box>

            <Dialog open={!!dialog} onClose={() => closeDialog(false)}>
                <DialogTitle>{dialog?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialog?.text}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => closeDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => closeDialog(true)}>Continue</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={saved} autoHideDuration={2400} onClose={() => setSaved(false)} anchorOrigin={{vertical: "bottom", horizontal: "center"}}>
                <Alert severity="success" onClose={() => setSaved(false)}>User saved successfully</Alert>
            </Snackbar>

            <ResetPasswordDialog
                open={resetPasswordOpen}
                onClose={() => setResetPasswordOpen(false)}
                userId={id}
                login={data.login}
                email={user?.email ?? ""}
            />
        </Box>
    );
};

export default UserDetails;
